import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Cable, LogIn, PlayCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LOGIN_BG = "https://images.pexels.com/photos/9701960/pexels-photo-9701960.jpeg";

export default function Login() {
  const { demoLogin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleGoogle = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    try {
      const { data } = await api.get("/auth/google/config");
      const redirectUrl = window.location.origin + "/auth/google";
      const params = new URLSearchParams({
        client_id: data.client_id,
        redirect_uri: redirectUrl,
        response_type: "code",
        scope: "openid email profile",
        access_type: "online",
        prompt: "select_account",
      });
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    } catch {
      toast.error("Configurazione Google non disponibile");
    }
  };

  const handleDemo = async () => {
    setLoading(true);
    try {
      await demoLogin();
      toast.success("Accesso demo effettuato");
      navigate("/dashboard");
    } catch {
      toast.error("Accesso demo non riuscito");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 relative border-r border-border overflow-hidden">
        <img src={LOGIN_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/85" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <img
              src={`${API}/settings/logo`}
              alt="Logo"
              data-testid="login-logo"
              className="h-11 w-auto max-w-[180px] object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
            />
            <div className="items-center gap-3" style={{ display: "none" }}>
              <div className="h-10 w-10 rounded-md bg-primary flex items-center justify-center">
                <Cable className="h-6 w-6 text-primary-foreground" strokeWidth={2} />
              </div>
              <span className="font-heading font-bold tracking-tight text-lg">LINK SRLS</span>
            </div>
          </div>
          <div>
            <h1 className="font-heading text-4xl font-extrabold tracking-tight leading-tight max-w-md">
              Gestionale impianti in fibra ottica
            </h1>
            <p className="text-muted-foreground mt-4 max-w-md text-sm leading-relaxed">
              Interventi, appalti, tipi di lavoro e squadre — tutto sincronizzato con l'app mobile,
              su un'interfaccia desktop pensata per il lavoro d'ufficio.
            </p>
          </div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            Dati sincronizzati · MongoDB Atlas
          </div>
        </div>
      </div>

      {/* Right auth panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-md bg-primary flex items-center justify-center">
              <Cable className="h-6 w-6 text-primary-foreground" strokeWidth={2} />
            </div>
            <span className="font-heading font-bold tracking-tight text-lg">LINK SRLS</span>
          </div>

          <div className="text-[10px] uppercase tracking-[0.18em] text-primary font-semibold mb-2">Accesso riservato</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight">Bentornato</h2>
          <p className="text-muted-foreground text-sm mt-2 mb-8">
            Accedi con il tuo account aziendale Google o prova la modalità demo.
          </p>

          <Button
            onClick={handleGoogle}
            data-testid="google-login-btn"
            className="w-full h-11 gap-2 font-semibold"
          >
            <LogIn className="h-4 w-4" strokeWidth={2} /> Accedi con Google
          </Button>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-border" />
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">oppure</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <Button
            onClick={handleDemo}
            disabled={loading}
            variant="outline"
            data-testid="demo-login-btn"
            className="w-full h-11 gap-2"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" strokeWidth={1.5} />}
            Entra in modalità Demo
          </Button>

          <p className="text-[11px] text-muted-foreground mt-8 leading-relaxed">
            I ruoli <span className="text-foreground">Responsabile</span> e{" "}
            <span className="text-foreground">Dipendente</span> vengono assegnati automaticamente
            in base all'account.
          </p>

          <div className="flex items-center gap-3 mt-6 text-[11px] text-muted-foreground">
            <a href="/privacy" className="hover:text-foreground underline underline-offset-2" data-testid="login-privacy-link">Privacy</a>
            <span>·</span>
            <a href="/terms" className="hover:text-foreground underline underline-offset-2" data-testid="login-terms-link">Termini di Servizio</a>
          </div>
        </div>
      </div>
    </div>
  );
}
